/// <reference lib="dom" />
//
// 🔴 THE ONE DOM-DEPENDENT FILE in this otherwise pure package. The package's
// tsconfig lib is `["ES2023"]` (no DOM) on purpose, so `geometry.ts` cannot
// quietly reach for a browser API. This directive pulls the DOM lib in FOR THE
// TYPECHECK because `parseGpx` uses `DOMParser`/`Document` — see the two-fact
// safety note below for why that never executes in Node.
/**
 * GPX → a route corridor's centre-line.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * MOVED HERE IN SLICE 24h-b — THE ONE COPY, SHARED BY web/ AND admin/.
 * ─────────────────────────────────────────────────────────────────────────────
 * This is a faithful port of what shipped in `web/src/features/zones/gpx.ts`
 * (slice 25). It lives in `@raad/geo` for the same reason `corridorShape` does:
 * a drifted second copy carries the single lat/lon → lon/lat swap and the point
 * cap, and a drift puts an imported route in the Indian Ocean. `web/`'s file is
 * now a one-line re-export of this; `admin/` imports it directly.
 *
 * 🔴 THE Node/DOMParser CONSTRAINT — TWO FACTS KEEP THIS SAFE, both verified:
 *   1. `api/` NEVER imports `@raad/geo`. The dependency edge is `geo → api`
 *      (type-only, for the wire contract), never back — so this function's
 *      reliance on `DOMParser` (a browser/DOM global absent in Node) is never
 *      reached on the server.
 *   2. `DOMParser` is referenced ONLY inside `parseGpx`'s body, never at module
 *      top level. So even a hypothetical Node import of the barrel (`@raad/geo`)
 *      evaluates this module without throwing — the global is only touched when
 *      the function is actually called, which happens only in a browser.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS PARSES A FILE A CUSTOMER CHOSE. TREAT IT AS HOSTILE INPUT.
 * ─────────────────────────────────────────────────────────────────────────────
 * A GPX file is arbitrary XML from a device, an app, or the internet. Three
 * things follow, and none of them are defensive padding:
 *
 *  1. **The point count is CAPPED.** A recorded track carries a point every few
 *     seconds — a day's driving is tens of thousands. Measured while planning
 *     slice 25: a 2001-point path buffers to a 4033-vertex polygon, which
 *     `ST_Covers` then walks on every position for every zone. Uncapped, one
 *     import degrades every other zone in the tenant.
 *  2. **Coordinates are RANGE-CHECKED.** A `lat="91"` is not a coordinate; it is
 *     a value PostGIS will reject later with a message about geography, far from
 *     the file that caused it.
 *  3. **Parse failure returns a REASON, never null.** "Could not read the file"
 *     is what a user gets from a null; "no track points found" tells them their
 *     file is a waypoint list, which is a thing they can fix.
 *
 * `DOMParser` is used rather than a regex: it does not resolve external
 * entities, and it is already in the browser.
 */

/** The maximum points accepted from one file — see note 1. */
export const MAX_GPX_POINTS = 10_000;

export type GpxResult =
  | { ok: true; coordinates: [number, number][]; pointCount: number; truncated: boolean }
  | { ok: false; reason: 'not_xml' | 'no_points' | 'too_few_points' | 'bad_coordinates' };

/**
 * Extract a route's centre-line from GPX text.
 *
 * Track points (`<trkpt>`) are preferred over route points (`<rtept>`): a track
 * is where a vehicle actually went, a route is where someone planned to go, and
 * when a file carries both the recorded one is the better description of the
 * corridor. Falls back to `<rtept>` when there is no track.
 *
 * Coordinates come back in GeoJSON order — **[lon, lat]**, not the [lat, lon]
 * the GPX attributes are written in. Getting this backwards puts a Mogadishu
 * route in the Indian Ocean, which is why the swap happens exactly once, here.
 */
export function parseGpx(text: string, maxPoints: number = MAX_GPX_POINTS): GpxResult {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(text, 'application/xml');
  } catch {
    return { ok: false, reason: 'not_xml' };
  }
  // DOMParser reports XML errors as a document containing <parsererror>, not by
  // throwing — a try/catch alone would accept a broken file.
  if (doc.querySelector('parsererror') !== null) return { ok: false, reason: 'not_xml' };

  let nodes = Array.from(doc.getElementsByTagName('trkpt'));
  if (nodes.length === 0) nodes = Array.from(doc.getElementsByTagName('rtept'));
  if (nodes.length === 0) return { ok: false, reason: 'no_points' };

  // The cap is a parameter so the truncation property can be proven at a small
  // boundary. Building a 10,000-point document in jsdom to assert one boolean
  // cost 23 s of a CI runner that is already the slowest thing in the loop.
  const truncated = nodes.length > maxPoints;
  const used = truncated ? nodes.slice(0, maxPoints) : nodes;

  const coordinates: [number, number][] = [];
  for (const n of used) {
    const lat = Number(n.getAttribute('lat'));
    const lon = Number(n.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { ok: false, reason: 'bad_coordinates' };
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      return { ok: false, reason: 'bad_coordinates' };
    }
    // GeoJSON order. Exactly one swap, in one place.
    coordinates.push([lon, lat]);
  }

  // A corridor needs a direction. One point is a place, and a place is a zone
  // of a different kind — it must not silently become a zero-length route.
  if (coordinates.length < 2) return { ok: false, reason: 'too_few_points' };

  return { ok: true, coordinates, pointCount: coordinates.length, truncated };
}
